/**
 * ProductsTab Component
 * Manage product catalog for sales capability
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Badge, Button, Modal, Table, Alert, Tab, Nav, Spinner } from 'react-bootstrap';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconUpload,
  IconDownload,
  IconSearch,
  IconPackage,
  IconCurrencyReal,
  IconPhoto,
  IconTag,
  IconBox,
  IconBrandGoogleDrive,
  IconApi,
  IconRefresh,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from '../../hooks/useAgentsFlowApi';
import { PRODUCT_CATALOG_SOURCES, PRODUCT_FIELDS } from '../../constants';
import './ProductsTab.css';

// ===============================================
// PRODUCT FORM MODAL
// ===============================================

const ProductFormModal = ({ show, onHide, product, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    category: '',
    stock: '',
    image_url: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        sku: product.sku || '',
        category: product.category || '',
        stock: product.stock || '',
        image_url: product.image_url || '',
        is_active: product.is_active ?? true,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        sku: '',
        category: '',
        stock: '',
        image_url: '',
        is_active: true,
      });
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Nome e preço são obrigatórios.',
      });
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        price: parseFloat(formData.price),
        stock: formData.stock ? parseInt(formData.stock) : null,
      });
      onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconPackage size={24} className="me-2" />
          {product ? 'Editar Produto' : 'Novo Produto'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row">
            <div className="col-md-8">
              <Form.Group className="mb-3">
                <Form.Label>Nome *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome do produto"
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>SKU/Código</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="SKU-001"
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrição detalhada do produto..."
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Preço (R$) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Estoque</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="Qtd"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Categoria</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Eletrônicos"
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>URL da Imagem</Form.Label>
            <Form.Control
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://..."
            />
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="switch"
              id="product-active"
              label="Produto ativo (visível para clientes)"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : <IconCheck size={18} className="me-1" />}
            Salvar
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ===============================================
// IMPORT MODAL
// ===============================================

const ImportModal = ({ show, onHide, onImport }) => {
  const [source, setSource] = useState('file');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Google Sheets config
  const [sheetsUrl, setSheetsUrl] = useState('');

  // API config
  const [apiUrl, setApiUrl] = useState('');
  const [apiHeaders, setApiHeaders] = useState('{}');

  const handleImport = async () => {
    setLoading(true);
    try {
      if (source === 'file') {
        fileInputRef.current?.click();
      } else if (source === 'google_sheets') {
        await onImport('google_sheets', { url: sheetsUrl });
        onHide();
      } else if (source === 'api') {
        await onImport('api', { url: apiUrl, headers: JSON.parse(apiHeaders) });
        onHide();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao importar',
        text: error.message || 'Não foi possível importar os produtos.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      await onImport('file', { file });
      onHide();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao importar',
        text: error.message || 'Não foi possível importar o arquivo.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconUpload size={24} className="me-2" />
          Importar Produtos
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
        />

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Fonte de Importação</Form.Label>
          <div className="import-sources">
            {PRODUCT_CATALOG_SOURCES.map((s) => (
              <Card
                key={s.id}
                className={`import-source-card ${source === s.id ? 'selected' : ''}`}
                onClick={() => setSource(s.id)}
              >
                <Card.Body className="text-center">
                  {s.id === 'manual' && <IconUpload size={24} />}
                  {s.id === 'google_sheets' && <IconBrandGoogleDrive size={24} />}
                  {s.id === 'api' && <IconApi size={24} />}
                  <h6 className="mt-2 mb-1">{s.name}</h6>
                  <small className="text-muted">{s.description}</small>
                </Card.Body>
              </Card>
            ))}
          </div>
        </Form.Group>

        {source === 'google_sheets' && (
          <Form.Group>
            <Form.Label>URL da Planilha</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://docs.google.com/spreadsheets/d/..."
              value={sheetsUrl}
              onChange={(e) => setSheetsUrl(e.target.value)}
            />
            <Form.Text className="text-muted">
              A planilha deve ter colunas: nome, descricao, preco, sku, categoria, estoque
            </Form.Text>
          </Form.Group>
        )}

        {source === 'api' && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>URL da API</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://api.sua-loja.com/produtos"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Headers (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={apiHeaders}
                onChange={(e) => setApiHeaders(e.target.value)}
                placeholder='{"Authorization": "Bearer token"}'
              />
            </Form.Group>
          </>
        )}

        {source === 'manual' && (
          <Alert variant="info">
            <IconUpload size={18} className="me-2" />
            Selecione um arquivo CSV ou Excel com os produtos. O arquivo deve ter colunas compatíveis.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={loading}>
          {loading ? <Spinner size="sm" className="me-2" /> : <IconUpload size={18} className="me-1" />}
          Importar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const ProductsTab = ({ agentId }) => {
  const { state, updateProducts } = useAgentsFlow();
  const { editor } = state;

  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load products
  useEffect(() => {
    if (editor?.products) {
      setProducts(editor.products);
    }
  }, [editor?.products]);

  // Filter products
  const filteredProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle save product
  const handleSaveProduct = useCallback(async (productData) => {
    const newProducts = editingProduct
      ? products.map((p) => (p.id === editingProduct.id ? { ...p, ...productData } : p))
      : [...products, { id: Date.now(), ...productData }];

    setProducts(newProducts);
    updateProducts(newProducts);
    setEditingProduct(null);

    Swal.fire({
      icon: 'success',
      title: 'Produto salvo!',
      timer: 1500,
      showConfirmButton: false,
    });
  }, [products, editingProduct, updateProducts]);

  // Handle delete product
  const handleDeleteProduct = useCallback(async (product) => {
    const result = await Swal.fire({
      title: 'Remover produto?',
      text: `"${product.name}" será removido do catálogo.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      const newProducts = products.filter((p) => p.id !== product.id);
      setProducts(newProducts);
      updateProducts(newProducts);
    }
  }, [products, updateProducts]);

  // Handle import
  const handleImport = useCallback(async (source, data) => {
    // In a real implementation, this would call an API
    // For now, we'll simulate it
    console.log('Importing from', source, data);

    Swal.fire({
      icon: 'success',
      title: 'Importação iniciada!',
      text: 'Os produtos serão processados em breve.',
      timer: 2000,
      showConfirmButton: false,
    });
  }, []);

  // Stats
  const activeCount = products.filter((p) => p.is_active).length;
  const totalStock = products.reduce((acc, p) => acc + (p.stock || 0), 0);

  return (
    <div className="tab-content-wrapper">
      {/* Stats */}
      <Alert variant="info" className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <IconPackage size={18} className="me-2" />
            <strong>{products.length}</strong> produtos | <strong>{activeCount}</strong> ativos
            {totalStock > 0 && <> | <strong>{totalStock}</strong> em estoque</>}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" onClick={() => setShowImportModal(true)}>
              <IconUpload size={16} className="me-1" />
              Importar
            </Button>
            <Button variant="primary" size="sm" onClick={() => { setEditingProduct(null); setShowProductModal(true); }}>
              <IconPlus size={16} className="me-1" />
              Novo Produto
            </Button>
          </div>
        </div>
      </Alert>

      {/* Search */}
      <div className="search-box mb-4">
        <IconSearch size={18} className="search-icon" />
        <Form.Control
          type="text"
          placeholder="Buscar produtos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Products Table */}
      {filteredProducts.length > 0 ? (
        <Card className="products-table-card">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Produto</th>
                <th>SKU</th>
                <th>Categoria</th>
                <th className="text-end">Preço</th>
                <th className="text-center">Estoque</th>
                <th className="text-center">Status</th>
                <th className="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="product-thumb"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="product-thumb-placeholder">
                          <IconPhoto size={16} />
                        </div>
                      )}
                      <div>
                        <div className="fw-medium">{product.name}</div>
                        {product.description && (
                          <small className="text-muted text-truncate d-block" style={{ maxWidth: 200 }}>
                            {product.description}
                          </small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {product.sku ? (
                      <Badge bg="light" text="dark">{product.sku}</Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {product.category ? (
                      <Badge bg="secondary">{product.category}</Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-end fw-medium">
                    R$ {parseFloat(product.price).toFixed(2)}
                  </td>
                  <td className="text-center">
                    {product.stock != null ? (
                      <Badge bg={product.stock > 0 ? 'success' : 'danger'}>
                        {product.stock}
                      </Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    <Badge bg={product.is_active ? 'success' : 'secondary'}>
                      {product.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                    >
                      <IconEdit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={() => handleDeleteProduct(product)}
                    >
                      <IconTrash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : (
        <Card className="text-center py-5">
          <Card.Body>
            <IconPackage size={48} className="text-muted mb-3" />
            <h5>Nenhum produto cadastrado</h5>
            <p className="text-muted mb-4">
              Adicione produtos manualmente ou importe de uma planilha/API.
            </p>
            <div className="d-flex justify-content-center gap-2">
              <Button variant="outline-primary" onClick={() => setShowImportModal(true)}>
                <IconUpload size={18} className="me-1" />
                Importar
              </Button>
              <Button variant="primary" onClick={() => { setEditingProduct(null); setShowProductModal(true); }}>
                <IconPlus size={18} className="me-1" />
                Adicionar Produto
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Modals */}
      <ProductFormModal
        show={showProductModal}
        onHide={() => { setShowProductModal(false); setEditingProduct(null); }}
        product={editingProduct}
        onSave={handleSaveProduct}
      />

      <ImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImport={handleImport}
      />
    </div>
  );
};

export default ProductsTab;
